import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import { DeleteOutlined, EditOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import type { CompanyProductAssociationListItem } from "../hooks/useGetCompanyProductAssociations"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import { PriorityStatus } from "@/graphql/codegen/graphql"

interface CustomerProductAssociationRowActionsProps {
  row: CellContext<CompanyProductAssociationListItem, unknown>["row"]
  onEdit: (item: CompanyProductAssociationListItem) => void
  onRemove: (item: CompanyProductAssociationListItem) => Promise<boolean>
}

export const CustomerProductAssociationRowActions: FC<CustomerProductAssociationRowActionsProps> = ({
  row,
  onEdit,
  onRemove,
}) => {
  const isDoNotUse = row.original.priorityStatus === PriorityStatus.DoNotUse

  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const handleOpen = () => setIsMenuOpen(true)
  const handleClose = () => setIsMenuOpen(false)

  const handleEditAssociation = () => {
    handleClose()
    onEdit(row.original)
  }

  const handleRemove = () => {
    handleClose()
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    const success = await onRemove(row.original)
    if (success) setIsDeleteDialogOpen(false)
  }

  return (
    <Box className='flex items-center justify-center'>
      <IconButton ref={anchorElRef} onClick={handleOpen} data-testid='association-row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleClose}
        data-testid='association-row-actions-menu'
      >
        <MenuItem onClick={handleEditAssociation} data-testid='edit-association-menu-item'>
          <ListItemIcon>
            <EditOutlined />
          </ListItemIcon>
          <ListItemText primary='Edit Association' />
        </MenuItem>
        {!isDoNotUse && (
          <MenuItem onClick={handleRemove} data-testid='remove-product-menu-item'>
            <ListItemIcon>
              <DeleteOutlined />
            </ListItemIcon>
            <ListItemText primary='Remove Product' />
          </MenuItem>
        )}
      </Menu>
      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title='Are you sure you want to remove this product association?'
          content='This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          isWarning
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  )
}
